import { Box, Divider, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useMemo, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useMSSP from "../../../../api/hooks/useMSSP";
import { entityKeys, platformUsersKeys } from "../../../../api/queryKeys";
import TenantAvatar from "../../../../components/mssp/TenantAvatar";
import ResizableDrawer from "../../../../components/ResizableDrawer";
import { EnvFeatureFlagContext } from "../../../../contexts/EnvFeaturesContext";
import { daysPastSince } from "../../../../utils/timestamp";
import { useEntities } from "../../../stack/environment/hooks/useEntities";
import { icons } from "../../../stack/environment/icons/iconExport";
import AssetsSection from "../../controls/components/AssetsSection/AssetsSection";
import MisconfiguredEntitiesSection from "../../controls/components/ControlMisconfiguration/relatedEntities/MisconfiguredEntitiesSection";
import VulnerabilityDrawerHeader from "./VulnerabilityDrawerHeader";

import LoadingIcon from "../../../../components/LoadingIcon";
import BreezeScore from "../components/BreezeScore";
import CompensatingControls from "./CompensatingControls";
import VulnerabilityMetadata from "./VulnerabilityMetadata";
import VulnerabilityQuickActions from "./VulnerabilityQuickActions";

const VulnerabilityDrawer = ({
  selectedVulnerability,
  openDrawer,
  onClose,
  topExposureDays,
}) => {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const [selectedEntityTypeTab, setSelectedEntityTypeTab] = useState(null);
  const {
    fetchEntitiesByVulnerability,
    getMSSPTenants,
    getTerminatedUsersAndHosts,
  } = useAPI();
  const { flattenEntities } = useEntities();
  const { isActiveRds } = useContext(EnvFeatureFlagContext);
  const { selectedTenantIds, msspId, hasMultipleSelectedTenants } = useMSSP();

  const { data: tenants = [] } = useQuery({
    queryKey: platformUsersKeys.msspTenants(),
    queryFn: () => getMSSPTenants(),
    // Sort tenants to show pinned ones first, with msspId at the top
    select: (response) =>
      [...response.data].sort((a, b) => {
        if (a.customer_id === msspId) return -1;
        if (b.customer_id === msspId) return 1;
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;
        return 0;
      }),

    enabled: hasMultipleSelectedTenants,
  });

  const {
    data: complexMisconfiguredEntitiesByType,
    isLoading: isLoadingMisconfigured,
  } = useQuery({
    queryKey: entityKeys.entitiesByVulnerability(
      selectedVulnerability?.vulnerability_id
    ),
    queryFn: async () => {
      try {
        const entities = (
          await fetchEntitiesByVulnerability(
            selectedVulnerability?.vulnerability_id
          )
        ).data;
        return entities;
      } catch (error) {
        return {};
      }
    },
    select: (response) => {
      const entities = flattenEntities(response);
      const entitiesByType = {};
      entities.forEach((entity) => {
        if (entity.entity_type in entitiesByType) {
          entitiesByType[entity.entity_type].push(entity);
        } else {
          entitiesByType[entity.entity_type] = [entity];
        }
      });
      return entitiesByType;
    },

    enabled: selectedVulnerability !== null,
  });

  const entityIds = useMemo(() => {
    if (!complexMisconfiguredEntitiesByType) return [];
    return Object.values(complexMisconfiguredEntitiesByType)
      .flat()
      .map((entity) => entity.entity_id);
  }, [complexMisconfiguredEntitiesByType]);

  const terminatedEntitiesQuery = useQuery({
    queryKey: entityKeys.terminatedEntities(),
    queryFn: getTerminatedUsersAndHosts,

    select: (response) => response?.data,
  });

  useEffect(() => {
    if (openDrawer && selectedVulnerability) {
      // NOTE: this track message is monitored by Mixpanel to collect data on user engagement - DO NOT CHANGE IT
      trackEvent(
        `User opened drawer for vulnerability id ${selectedVulnerability.vulnerability_id}`
      );
    }
  }, [openDrawer, selectedVulnerability]);

  if (
    openDrawer &&
    complexMisconfiguredEntitiesByType &&
    terminatedEntitiesQuery.isSuccess &&
    Object.keys(complexMisconfiguredEntitiesByType).length > 0 &&
    (!selectedEntityTypeTab ||
      !complexMisconfiguredEntitiesByType[selectedEntityTypeTab])
  )
    setSelectedEntityTypeTab(
      Object.keys(complexMisconfiguredEntitiesByType)[0]
    );

  const haveInternetFacingEntities = () => {
    const allEntities = Object.values(
      complexMisconfiguredEntitiesByType
    ).flat();
    // Return "unknown" if all entities are undefined for is_internet_facing
    if (
      allEntities.every(
        (entity) =>
          entity?.is_internet_facing === null ||
          entity?.is_internet_facing === undefined
      )
    ) {
      return "unknown";
    }
    return allEntities.some((entity) => entity?.is_internet_facing === true);
  };

  const isShowWarningIcon = () => {
    return (
      daysPastSince(
        selectedVulnerability?.published_date?.timestamp_utc_human_readable
      ) > topExposureDays
    );
  };

  return (
    <ResizableDrawer
      initialWidth={1100}
      minWidth={1100}
      PaperProps={{
        style: {
          borderRadius: "8px 0 0 0",
        },
      }}
      anchor="right"
      open={openDrawer}
      onClose={onClose}
    >
      {selectedVulnerability && (
        <Box
          sx={{
            width: "100%",
            padding: "32px",
            backgroundColor: colors.grey_50,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 4,
            overflowY: "auto",
          }}
        >
          <Box display="flex" flexDirection="column" gap={2}>
            <VulnerabilityDrawerHeader
              selectedVulnerability={selectedVulnerability}
            />
            <VulnerabilityMetadata
              selectedVulnerability={selectedVulnerability}
              isLoadingMisconfigured={isLoadingMisconfigured}
              complexMisconfiguredEntitiesByType={
                complexMisconfiguredEntitiesByType
              }
              colors={colors}
              isShowWarningIcon={isShowWarningIcon}
              icons={icons}
              haveInternetFacingEntities={haveInternetFacingEntities}
              datePublished={
                selectedVulnerability?.published_date
                  ?.timestamp_utc_human_readable
              }
            />
            <VulnerabilityQuickActions
              selectedVulnerability={selectedVulnerability}
            />
            <Divider orientation="horizontal" />
          </Box>
          {!isActiveRds && [
            <BreezeScore selectedVulnerability={selectedVulnerability} />,
          ]}
          {!hasMultipleSelectedTenants && [
            <AssetsSection
              affected_assets_IDs={selectedVulnerability.related_asset_ids}
              source="vulnerability"
            />,
          ]}
          {hasMultipleSelectedTenants && (
            <Box display={"flex"} flexDirection={"column"} gap={3}>
              <Typography variant="subtitle1">
                What Tenants are affected?
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "start",
                  gap: 1,
                }}
              >
                {selectedVulnerability.customer_ids.map((customerId) => {
                  const tenant = tenants?.find(
                    (tenant) => tenant.customer_id === customerId
                  );
                  return (
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      alignItems={"start"}
                      gap={1}
                      key={customerId}
                    >
                      <TenantAvatar tenant={tenant} />
                      <Typography variant="caption">
                        {tenant?.company_name}
                      </Typography>
                    </Box>
                  );
                })}
              </Typography>
            </Box>
          )}
          {complexMisconfiguredEntitiesByType &&
            terminatedEntitiesQuery.isSuccess &&
            selectedEntityTypeTab && (
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="subtitle1">
                  What entities are at risk?
                </Typography>
                <MisconfiguredEntitiesSection
                  entitiesByType={complexMisconfiguredEntitiesByType}
                  showDetailedAffectedEntities={true}
                  selectedEntityTypeTab={selectedEntityTypeTab}
                  setSelectedEntityTypeTab={setSelectedEntityTypeTab}
                  hasMultipleSelectedTenants={hasMultipleSelectedTenants}
                />
              </Box>
            )}
          {isLoadingMisconfigured && <LoadingIcon size={12} />}
          <CompensatingControls
            selectedVulnerability={selectedVulnerability}
            selectedTenantIds={selectedTenantIds}
            hasMultipleSelectedTenants={hasMultipleSelectedTenants}
            entityIds={entityIds}
          />
        </Box>
      )}
    </ResizableDrawer>
  );
};

export default VulnerabilityDrawer;
